import type {
	DigitalEditionData,
	DigitalEditionPositionsData,
	PrintEditionData,
	ProductData
} from '../../types/types.ts';
import {AmediaContentAdapter} from '../adapters/amedia-adapter.ts';
import {DefaultContentAdapter} from '../adapters/default-content-adapter.ts';
import {DnContentAdapter} from '../adapters/dn-adapter.ts';
import {NordjyskeContentAdapter} from '../adapters/nordjyske-content-adapter.ts';
import {getClientId} from '../lib/storage.ts';
import type {Store} from '../lib/store.ts';
import {loadRecentEditionsForProduct} from '../services/content.ts';
import type {Article} from './article.ts';
import {ContentSourceEdition} from './content-source-edition.ts';
import {DigitalEdition} from './digital-edition.ts';
import {PrintEdition} from './print-edition.ts';
import type {ReaderEdition} from './reader-edition.ts';
import {ReelsEdition} from './reels-edition.ts';
import {SingleArticleEdition} from './single-article-edition.ts';
import {injectSummaryArticle} from './summary-injector.ts';

export async function fromPlaylist(resourceId: string) {
	const edition = new ReelsEdition(resourceId, getContentAdapter(getClientId()));
	await edition.loadPlaylist();
	return edition;
}

const productNameArticleIdPrefixMapping: Record<string, string> = {
	'dn-dn-print-automation': '2-1-'
};

export async function fromPrintEdition(printEditionData: PrintEditionData, productData: ProductData) {
	const edition = new PrintEdition(printEditionData, productData, getContentAdapter(getClientId()));
	edition.articleIdPrefix = productNameArticleIdPrefixMapping[productData.attributes.name] || '';

	const recentEditionsResponse = await loadRecentEditionsForProduct(productData.attributes.name, 15);
	if (recentEditionsResponse.status === 200) {
		edition.latestEditions = recentEditionsResponse.body.sort((a, b) => a.publishDate.localeCompare(b.publishDate));
	}

	return edition;
}

export async function fromContentSource(
	sourceId: string,
	queryId: string,
	date: string,
	tag: string,
	articleId: string
) {
	const edition = new ContentSourceEdition(sourceId, queryId, date, tag, articleId, getContentAdapter(getClientId()));
	await edition.loadContentSource();

	return withSummary(edition, true);
}

export async function fromDigitalEdition(
	editionData: DigitalEditionData,
	positionsData: DigitalEditionPositionsData,
	productData: ProductData
) {
	const edition = new DigitalEdition(editionData, positionsData, productData, getContentAdapter(getClientId()));
	return withSummary(edition, false);
}

export async function fromSingleArticle(
	sourceId: string | undefined,
	articleId: string,
	store: Store,
	data: string
): Promise<SingleArticleEdition> {
	const adapter = getContentAdapter(getClientId());

	let article: Article;
	if (data) {
		article = adapter.loadSingleItemFromFixture(data, store);
	} else {
		article = await adapter.loadSingleItem(articleId, sourceId || adapter.singleItemSourceId, store);
	}

	return new SingleArticleEdition(article, adapter);
}

function getContentAdapter(clientId: string) {
	switch (clientId) {
		case 'dn':
			return new DnContentAdapter();
		case 'broadcast':
		case 'amedia':
			return new AmediaContentAdapter();
		case 'nordjyske':
			return new NordjyskeContentAdapter();
		default:
			return new DefaultContentAdapter();
	}
}

export function getLocaleForClient(clientId: string): string {
	return getContentAdapter(clientId).locale;
}

export function getTimezoneForClient(clientId: string): string {
	return getContentAdapter(clientId).timezone;
}

/**
 * Wraps an edition to inject AI summary as the first article.
 * Modifies loadArticleList to add summary after articles are loaded.
 */
function withSummary<T extends ReaderEdition>(edition: T, isContentSource: boolean): T {
	const originalLoadArticleList = edition.loadArticleList.bind(edition);

	edition.loadArticleList = async (store: Store) => {
		const articles = await originalLoadArticleList(store);
		return injectSummaryArticle(edition, articles, isContentSource);
	};

	return edition;
}
